/**
 * The four dimensions an agent is typed on, and the values each one takes.
 *
 * <h3>Why they live here</h3>
 *
 * V188 replaced the old single `type` field with four columns, each carrying a
 * CHECK constraint — the backend rejects a create whose value is outside its
 * enum. The lists were written out inside the create slideout, and Protect's
 * build spine needs the same four questions. A second copy is a second answer
 * to "what may this be set to", and the one that drifts is found by a 500.
 *
 * The AutoCreate launcher still carries its own copies. They are NOT identical
 * — its execution list adds `auto`, meaning "let the pipeline choose from the
 * source" — so they are left alone rather than merged into a list that would be
 * wrong for one of the two callers.
 */

export const FUNCTION_MODE_OPTIONS = [
  { value: 'general',       label: 'general — catch-all / cross-functional' },
  { value: 'compliance',    label: 'compliance — evidence collection' },
  { value: 'threat',        label: 'threat — SOC / IR triage' },
  { value: 'iam',           label: 'iam — access governance' },
  { value: 'appsec',        label: 'appsec — code + dependency review' },
  { value: 'devops',        label: 'devops — platform engineering, infra automation' },
  { value: 'secops',        label: 'secops — security operations, detection engineering' },
  /* Step 1 (Platform-Eng workflow) — control-plane discipline. Used
   * for agents that govern OTHER agents (fleet controllers,
   * orchestrators, runbook coordinators). Backend whitelist
   * extended in V205. When selected, picks opinionated defaults
   * for the other two modes (operation=human, execution=deterministic);
   * those are stamped only if the operator hasn't explicitly changed
   * the corresponding dropdown — manual choices always win. */
  { value: 'control_plane', label: 'control_plane — platform-engineering controller (protects other agents; supervised)' },
];
export const INDUSTRY_MODE_OPTIONS = [
  { value: 'general',       label: 'general — industry-agnostic' },
  { value: 'financial',     label: 'financial — banking, capital markets, payments' },
  { value: 'insurance',     label: 'insurance — P&C, life, health, reinsurance' },
  { value: 'healthcare',    label: 'healthcare — providers, payers, HIPAA-bound' },
  { value: 'retail',        label: 'retail — commerce, supply chain' },
  { value: 'telco',         label: 'telco — carriers, network operators' },
  { value: 'government',    label: 'government — public sector, regulated agency' },
  { value: 'energy',        label: 'energy — utilities, oil & gas, NERC-CIP' },
  { value: 'manufacturing', label: 'manufacturing — industrial, OT/ICS' },
];
export const OPERATION_MODE_OPTIONS = [
  { value: 'platform',   label: 'platform — platform-orchestrated, no human in loop' },
  { value: 'human',      label: 'human — every action gated by human approval' },
  { value: 'autonomous', label: 'autonomous — self-directed within policy (requires Full trust)' },
];
export const EXECUTION_MODE_OPTIONS = [
  { value: 'deterministic', label: 'Deterministic — workflow-driven, LLM invoked at skill boundaries (default)' },
  { value: 'cognitive',     label: 'Cognitive — LLM-driven reasoning loop with available-tool catalog' },
  { value: 'hybrid',        label: 'Hybrid — deterministic head/tail around a cognitive body' },
];
